<template>
  <b-row>
    <b-col cols="12" md="10" offset-md="1">
      <b-card class="shadow px-1 py-2 mb-5">

        <onboarding-stepper
          v-if="currentStep.stepper"
          :currentStep="currentStep"
          :steps="steps"
        />

        <b-row v-if="isIntro" class="pt-5 pb-4 px-lg-5">
          <b-col cols="12" class="px-4 px-lg-5">
            <h2 v-if="templates.introTitle" class="text-center px-lg-5">{{ templates.introTitle }}</h2>
            <h2 v-else class="text-center px-lg-5">Is Mentorship for you?</h2>
            <h4 v-if="templates.introSubtitle" class="text-center px-lg-5 pt-3" style="line-height: 1.8" v-html="templates.introSubtitle"></h4>
            <h4 v-else class="text-center px-lg-5 pt-4" style="line-height: 1.8">We are always looking for great mentors to help us at RealLinks. Mentoring can help people get the most out of their lives and careers, as well as learn and grow.</h4>
          </b-col>
          <b-col
            v-if="company && company.branding && company.branding.onboardingMentorshipImage && company.branding.onboardingMentorshipImage.fileLocation"
            cols="12" md="6" lg="4" class="px-4 py-4 pl-lg-5"
          >
            <b-img
              :src="company.branding.onboardingMentorshipImage.fileLocation"
              fluid
            ></b-img>
          </b-col>
          <b-col cols="12" md="6" lg="8" class="px-4 py-4 pr-lg-5">
            <p v-if="templates.introDescription" v-html="templates.introDescription"></p>
            <p v-else>To become a mentor you must first read our mentorship policy and familiarise yourself with the process, training and expectations.<br><br>If you are still keen to become a mentor, please provide your details on the next page.<br><br>This will then be submitted to the mentorship team who will review your application. Once you've been approved you'll then be visible on the platform to colleagues who can request to be put in touch.</p>
          </b-col>
        </b-row>

        <b-row v-else class="pt-5 pb-4 px-5">
          <b-col lg="4" class="px-4">
            <b-jumbotron>
              <p v-if="templates.title"><b>{{ templates.title }}</b></p>
              <p><b>Mentorship</b></p>
              <p v-if="templates.description" v-html="templates.description"></p>
              <p>Interested in mentoring others? Why not let us know what kind of mentor you’d like to be and we’ll connect you to those in need.</p>
            </b-jumbotron>
            <div class="py-3 px-3">
              <b-img
                v-if="company && company.branding && company.branding.onboardingMentorshipImage && company.branding.onboardingMentorshipImage.fileLocation"
                :src="company.branding.onboardingMentorshipImage.fileLocation"
                fluid
              ></b-img>
            </div>
          </b-col>
          <b-col lg="8" class="px-4">
            <b-alert show variant="danger" v-if="!canSubmit" class="mt-3">
              Please fill in all the required fields to continue.
            </b-alert>

            <div
              v-for="({ key, name, description, type, options, departments, required }) in customFields"
              :key="key"
              class="mb-5"
            >
              <hr v-if="type === 'TEXTAREA'" />
              <div v-if="['STRING', 'ENUM', 'LOCATION', 'RANGE', 'TEXTAREA'].includes(type) && (!departments || departments.filter(id => departmentIds.includes(id)).length)">
                <h4><b>{{name}}</b></h4>
                <p class="text-muted">{{description}}</p>
                <b-form-group
                  :label-for="`input-${key}`"
                >
                  <b-form-select
                    v-if="type === 'ENUM' && options"
                    :id="`input-${key}`"
                    type="text"
                    :required="required"
                    :options="options"
                    :value="inputValues[key]"
                    @input="value => $set(inputValues, key, value)"
                  >
                      <template v-slot:first><option :value="null">-- Please select an option --</option></template>
                  </b-form-select>
                  <b-form-input
                    v-if="type === 'RANGE'"
                    id="`input-${key}`"
                    :value="inputValues[key] || 3"
                    @input="value => $set(inputValues, key, value)"
                    type="range"
                    min="1"
                    max="10"
                  ></b-form-input>
                  <p class="text-right" v-if="type === 'RANGE'">{{ inputValues[key] || 3 }} {{ inputValues[key] && inputValues[key] === 1 ? 'person' : 'people' }}</p>
                  <b-form-input
                    v-if="type === 'STRING'"
                    :id="`input-${key}`"
                    type="text"
                    :required="required"
                    :value="inputValues[key]"
                    @input="value => $set(inputValues, key, value)"
                  />
                  <location-search
                    v-if="type === 'LOCATION'"
                    :value="inputValues[key] ? inputValues[key].location : null"
                    @input="value => $set(inputValues, key, {
                      remote: inputValues[key] ? inputValues[key].remote : null,
                      location: value,
                    })"
                  />
                  <b-form-checkbox
                    v-if="type === 'LOCATION'"
                    :checked="inputValues[key] ? inputValues[key].remote : false"
                    @input="value => $set(inputValues, key, {
                    remote: value,
                    location: inputValues[key] ? inputValues[key].location : null,
                    })"
                    size="lg"
                  ><span class="pt-2"> Are you interested in opportunities with remote working?</span></b-form-checkbox>
                </b-form-group>
                <b-form-textarea
                  v-if="type === 'TEXTAREA'"
                  :id="`input-${key}`"
                  type="text"
                  :required="required"
                  :value="inputValues[key]"
                  @input="value => $set(inputValues, key, value)"
                  rows="3"
                  max-rows="6"
                ></b-form-textarea>
              </div>
              <div class="d-flex justify-content-between w-100" v-if="type === 'CHECKBOX'">
                <div>
                  <h4><b>{{name}}</b></h4>
                  <p class="text-muted">{{description}}</p>
                </div>
                <b-form-checkbox
                  :checked="inputValues[key]"
                  @input="value => $set(inputValues, key, value)"
                  size="lg"
                >
                </b-form-checkbox>
              </div>
              <job-title-selector
                v-if="type === 'JOB_TITLES'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                @input="value => inputValues[key] = value"
              />
              <job-business-area-selector
                v-if="type === 'JOB_BUSINESS_AREA'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                :multiple="true"
                :settings="{ noTitle: true }"
                @input="value => inputValues[key] = value"
              />
              <job-skills-selector
                v-if="type === 'JOB_SKILLS'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                :isFreeText="!$root.isCXR"
                @input="skillsInput($event, key, 'SKILL')"
              />
              <languages-selector
                v-if="type === 'LANGUAGES'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :required="required"
                @input="value => inputValues[key] = value"
              />
              <qualifications-editor
                v-if="type === 'QUALIFICATIONS'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :required="required"
                @input="value => inputValues[key] = value"
              />
            </div>
          </b-col>
        </b-row>

        <b-row class="px-5">
          <b-col class="w-100 px-4">
            <a class="float-left pt-2 text-secondary" href="#" @click.prevent="() => { if (isIntro || $root.isCXR) { $emit('goBack') } else { isIntro = true } }">Go back</a>
            <b-button @click.prevent="submitForm" class="float-right px-5" variant="primary">{{ isIntro ? 'Interested in being a mentor' : 'Continue' }}</b-button>
            <b-button v-if="isIntro" class="float-right px-5 mr-3" variant="outline-primary" @click.prevent="() => $emit('goNext')">Skip</b-button>
          </b-col>
        </b-row>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import { mapRoot } from '@src/mixins/root';
import OnboardingStepper from '@/components/profile/OnboardingStepper';
import JobTitleSelector from '@/components/profile/JobTitleSelector';
import JobSkillsSelector from '@/components/profile/JobSkillsSelector';
import LanguagesSelector from '@/components/profile/LanguagesSelector';
import QualificationsEditor from '@/components/profile/QualificationsEditor';
import JobBusinessAreaSelector from '@/components/profile/JobBusinessAreaSelector';
import LocationSearch from '@/components/misc/LocationSearch';
import RegisterQuestionsQuery from '@/graphql/queries/RegisterQuestions.gql';
import gql from 'graphql-tag';

const templateMap = {
  ONBOARDING_MENTORSHIP_INTRO_TITLE: 'introTitle',
  ONBOARDING_MENTORSHIP_INTRO_SUBTITLE: 'introSubtitle',
  ONBOARDING_MENTORSHIP_INTRO_DESCRIPTION: 'introDescription',
  ONBOARDING_MENTORSHIP_TITLE: 'title',
  ONBOARDING_MENTORSHIP_DESCRIPTION: 'description',
};

const WriteUserQuestionsMutation = gql`
  mutation WriteUserCustomFields($update: [ UpdateUserCustomFieldInput! ]!) {
    updateManyUserCustomFields(update: $update) {
      key
      value
    }
  }
`;

const UpdateMentorStatusMutation = gql`
  mutation updateUser($userID: ID) {
    updateUser(update: {
      mentorStatus: REQUESTED
    },
    where: {
      id: $userID
    }) {
      id
    }
  }
`;

const AddTaxonomySkillSuggestionsMutation = gql`
  mutation AddTaxonomySkillsSuggestions($skills: [String!]) {
    createTaxonomySkillSuggestions(skills: $skills)
  }
`;

export default {
  name: 'OnboardingMentorship',
  components: {
    OnboardingStepper,
    JobTitleSelector,
    JobSkillsSelector,
    LanguagesSelector,
    QualificationsEditor,
    LocationSearch,
    JobBusinessAreaSelector,
  },
  props: {
    currentStep: {
      type: Object,
      default: () => null,
    },
    steps: {
      type: Array,
      default: () => [],
    }
  },
  data() {
    return {
      fatalErr: null,
      err: null,
      inputValues: {},
      canSubmit: true,
      isIntro: !this.$root.isCXR,
      skillSuggestions: [],
    };
  },
  apollo: {
    company: {
      query: RegisterQuestionsQuery,
      update: ({ company }) => company,
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { company, user } = data;
          this.$assert(company && company.id, 500, new Error('Failed to load company register questions'));
          this.$assert(user && user.id, 500, new Error('Failed to load user register questions'));
          if (user && Array.isArray(user.customFields) && user.customFields.length) {
            this.inputValues = user.customFields.reduce((values, { key, value }) => {
              try {
                value = JSON.parse(value);
              } catch (e) {
                // not a json
              }
              return Object.assign(values, { [key]: value });
            }, {});
          }
        }
      },
      error(err) {
        this.fatalErr = err;
      }
    },
  },
  computed: {
    ...mapRoot([ 'user' ]),
    templates() {
      const templates = {};
      if (this.company && Array.isArray(this.company.templatedStrings)) {
        const { templatedStrings } = this.company;
        Object.keys(templateMap).forEach(templateKey => {
          const template = templatedStrings.find(el => el.key === templateKey);
          templates[templateMap[templateKey]] = template ? template.value : null;
        });
      }
      console.log(templates);
      return templates;
    },
    departmentIds() {
      const { departments } = this.user;
      return Array.isArray(departments) ? departments.map(({ id }) => id) : [];
    },
    customFields() {
      const { customFields } = this.company || {};
      const fields = (Array.isArray(customFields) ? customFields : []).map(({ options, departments, ...field }) => ({
        ...field,
        options: options ? JSON.parse(options) : null,
        departments: departments ? departments.map(({ id }) => id) : null,
      })).filter(field => field.group === this.currentStep.name);
      console.log(fields);
      return fields;
    },
  },
  methods: {
    async submitForm() {
      try {
        if (this.isIntro) {
          this.isIntro = false;
          return;
        }

        const { inputValues, customFields } = this;

        if (Object.keys(inputValues).length) {
          this.canSubmit = customFields.reduce((out, el) => {
            if (el.required) {
              console.log('required field: ', inputValues[el.key]);
              const val = inputValues[el.key];
              out = out && ((Array.isArray(val) && val.length) || (!Array.isArray(val) && val));
            }
            return out;
          }, true);
          if (!this.canSubmit) return;

          await this.$apollo.mutate({
            mutation: WriteUserQuestionsMutation,
            variables: {
              update: Object.keys(inputValues).map(key => ({
                key,
                value: typeof inputValues[key] === 'string' ? inputValues[key] : (inputValues[key] ? JSON.stringify(inputValues[key]) : null)
              })).filter(({ value }) => value),
            },
            update(store, { data: { updateManyUserCustomFields: fields } }) {
              const data = store.readQuery({ query: RegisterQuestionsQuery });
              data.user.customFields = data.user.customFields.map(field => {
                if (inputValues[field.key]) {
                  field.value = inputValues[field.key];
                }
                return field;
              });
              store.writeQuery({ query: RegisterQuestionsQuery, data });
            },
          });

          const { skillSuggestions } = this;
          if (!this.$root.isCXR && Array.isArray(skillSuggestions) && skillSuggestions.length) {
            await this.$apollo.mutate({
              mutation: AddTaxonomySkillSuggestionsMutation,
              variables: { skills: skillSuggestions }
            });
          }

          if (!this.$root.isOnlyMentorship && !this.$root.isCXR && ![ 'REQUESTED', 'ACTIVE', 'INACTIVE' ].includes(this.user.mentorStatus)) {
            await this.$apollo.mutate({
              mutation: UpdateMentorStatusMutation,
              variables: {
                userID: this.user.id,
              },
            });
          }

          Promise.resolve(this.$root.captureLog({
            eventName: 'IM_ONBOARDING_MENTORSHIP_SUCCESS',
            user: { self: true },
          }));
        }

        this.$emit('goNext');
      } catch (err) {
        console.error(err);
        this.err = err;
      }
    },
    skillsInput(input, key, type) {
      if (this.$root.isCXR) {
        this.inputValues[key] = input;
      } else {
        this.inputValues[key] = input.input;
        this.skillSuggestions = input.newTerms;
        console.log('NEW SKILL SUGGESTIONS FROM INPUT: ', this.skillSuggestions);
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/onboarding/Mentorship.vue